import React, { useState } from 'react';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { Badge, Btn, fmtDollar } from '../components/UI';
import { useAuth, ROLES } from '../data/auth';
import supabase from '../data/supabase';

export default function UserProfile({ onClose }) {
  const auth = useAuth();
  const user = auth.currentUser;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    title: user?.title || "",
    timezone: user?.timezone || "America/New_York",
  });
  const [changingPw, setChangingPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new1: "", new2: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [saved, setSaved] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const [voicemailSetting, setVoicemailSetting] = useState("org_default");
  const [voicemailSaved, setVoicemailSaved] = useState(false);

  // Load user's personal voicemail preference
  React.useEffect(() => {
    if (!user?._dbId && !user?.id) return;
    (async () => {
      try {
        const uid = user._dbId || user.id;
        const { data } = await supabase.from("user_preferences").select("*").eq("user_id", uid).eq("key", "voicemail").maybeSingle();
        if (data?.value) setVoicemailSetting(data.value);
      } catch (e) {}
    })();
  }, [user]);

  const saveVoicemail = async (val) => {
    setVoicemailSetting(val);
    try {
      const uid = user._dbId || user.id;
      await supabase.from("user_preferences").upsert({
        user_id: uid, key: "voicemail", value: val, updated_at: new Date().toISOString(),
      }, { onConflict: "user_id,key" });
      setVoicemailSaved(true);
      setTimeout(() => setVoicemailSaved(false), 2000);
    } catch (e) { console.warn("[Profile] Voicemail save:", e.message); }
  };

  if (!user) return null;

  const role = ROLES[user.role] || {};
  const avatarUrl = user.avatarUrl || user.avatar_url;
  const initials = (user.firstName?.[0] || "") + (user.lastName?.[0] || "");

  const handleSave = async () => {
    // Persist to Supabase users table (source of truth)
    const uid = user._dbId || user.id;
    if (uid) {
      try {
        const { error } = await supabase.from("users").update({
          first_name: form.firstName,
          last_name: form.lastName,
          phone: form.phone,
          title: form.title,
          timezone: form.timezone,
          updated_at: new Date().toISOString(),
        }).eq("id", uid);
        if (error) { console.error("[Profile] Save failed:", error.message); alert("Failed to save: " + error.message); return; }
      } catch (e) { console.error("[Profile] Save error:", e.message); alert("Failed to save: " + e.message); return; }
    }
    // Update in-memory auth state so header/nav reflect changes immediately
    auth.updateUser(user.id, {
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      title: form.title,
      timezone: form.timezone,
      avatar: (form.firstName?.[0] || "") + (form.lastName?.[0] || ""),
    });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePwChange = async () => {
    if (pwForm.new1.length < 6) {
      setPwMsg("New password must be at least 6 characters");
      return;
    }
    if (pwForm.new1 !== pwForm.new2) {
      setPwMsg("New passwords don't match");
      return;
    }
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.new1 });
      if (error) { setPwMsg(error.message); return; }
      setPwMsg("");
      setChangingPw(false);
      setPwForm({ current: "", new1: "", new2: "" });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setPwMsg(e.message || "Failed to update password");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Image too large (max 2MB)"); return; }
    try {
      const ext = file.name.split(".").pop();
      const path = `avatars/${user._dbId || user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatar").upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("avatar").getPublicUrl(path);
      if (urlData?.publicUrl) {
        await supabase.from("users").update({ avatar_url: urlData.publicUrl }).eq("id", user._dbId || user.id);
        auth.updateUser(user.id, { avatarUrl: urlData.publicUrl, avatar_url: urlData.publicUrl });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      console.warn("[Profile] Avatar upload failed:", err.message);
    }
  };

  const card = {
    background: "#2C2E33",
    border: "1px solid rgba(217,219,221,0.10)",
    borderRadius: 8,
    padding: 20,
    marginBottom: 12,
  };
  const inp = {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)",
    color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };
  const lbl = { fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.35)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 5, display: "block" };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", margin: 0 }}>My Profile</h2>
          <p style={{ fontSize: 12, color: "rgba(250,250,250,0.35)", margin: "4px 0 0" }}>Manage your account settings</p>
        </div>
        {saved && <span style={{ fontSize: 12, color: "#2E8B57", display: "flex", alignItems: "center", gap: 4 }}><Icon name="check" size={14} color="#2E8B57" /> Saved</span>}
      </div>

      {/* Profile Card */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          {/* Avatar with upload */}
          <label style={{ cursor: "pointer", position: "relative" }}
            onMouseEnter={() => setAvatarHover(true)} onMouseLeave={() => setAvatarHover(false)}>
            <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: "none" }} />
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{
                width: 64, height: 64, borderRadius: 32, objectFit: "cover",
                border: "2px solid rgba(64,89,173,0.3)",
              }} />
            ) : (
              <div style={{
                width: 64, height: 64, borderRadius: 32, display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(64,89,173,0.15)", border: "2px solid rgba(64,89,173,0.3)",
                fontSize: 20, fontWeight: 400, color: "#4059AD",
              }}>{initials}</div>
            )}
            {avatarHover && (
              <div style={{
                position: "absolute", inset: 0, borderRadius: 32, background: "rgba(0,0,0,0.5)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name="upload" size={16} color="#FAFAFA" />
              </div>
            )}
          </label>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA" }}>{user.firstName} {user.lastName}</div>
            <div style={{ fontSize: 12, color: "rgba(250,250,250,0.45)", marginTop: 2 }}>{user.title ? user.title + " · " : ""}{user.email}</div>
          </div>
          <button onClick={() => setEditing(!editing)} style={{
            padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
            background: "transparent", color: "#FAFAFA", fontSize: 12, fontWeight: 400,
            cursor: "pointer", fontFamily: "inherit",
          }}>{editing ? "Cancel" : "Edit"}</button>
        </div>

        {editing ? (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>First Name</label><input style={inp} value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} /></div>
              <div><label style={lbl}>Last Name</label><input style={inp} value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>Job Title</label><input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label style={lbl}>Phone</label><input style={inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Timezone</label>
              <select style={inp} value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
                <option value="America/New_York">Eastern (ET) — America/New_York</option>
                <option value="America/Chicago">Central (CT) — America/Chicago</option>
                <option value="America/Denver">Mountain (MT) — America/Denver</option>
                <option value="America/Phoenix">Arizona (no DST) — America/Phoenix</option>
                <option value="America/Los_Angeles">Pacific (PT) — America/Los_Angeles</option>
                <option value="America/Anchorage">Alaska — America/Anchorage</option>
                <option value="Pacific/Honolulu">Hawaii — Pacific/Honolulu</option>
                <option value="Europe/London">London — Europe/London</option>
                <option value="Europe/Berlin">Berlin — Europe/Berlin</option>
                <option value="Asia/Tokyo">Tokyo — Asia/Tokyo</option>
                <option value="Australia/Sydney">Sydney — Australia/Sydney</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Save Changes</button>
              <button onClick={() => setEditing(false)} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.5)", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { l: "Email", v: user.email },
              { l: "Phone", v: user.phone || "—" },
              { l: "Role", v: role.label || user.role },
              { l: "Member Since", v: user.createdAt ? (() => { const d = new Date(user.createdAt); return String(d.getMonth()+1).padStart(2,"0")+"/"+String(d.getDate()).padStart(2,"0")+"/"+String(d.getFullYear()).slice(-2); })() : "—" },
              { l: "Last Login", v: user.lastLogin?.slice(0, 16)?.replace("T", " ") || "—" },
              { l: "Quota", v: user.quota ? fmtDollar(user.quota) : "—" },
            ].map(item => (
              <div key={item.l}>
                <div style={{ fontSize: 10, color: "rgba(250,250,250,0.3)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>{item.l}</div>
                <div style={{ fontSize: 13, color: "#FAFAFA" }}>{item.v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call Settings */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>Call Settings</div>
            <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", marginTop: 2 }}>Personal call preferences. Overrides org defaults where applicable.</div>
          </div>
          {voicemailSaved && <span style={{ fontSize: 11, color: "#2E8B57" }}>Saved</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.35)", letterSpacing: ".06em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Voicemail</label>
            <select value={voicemailSetting} onChange={e => saveVoicemail(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
              <option value="org_default">Use org default</option>
              <option value="on">Enabled — record voicemail after timeout</option>
              <option value="off">Disabled — no voicemail</option>
            </select>
            <div style={{ fontSize: 9, color: "rgba(250,250,250,0.25)", marginTop: 4 }}>Overrides the org setting for your calls only.</div>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.35)", letterSpacing: ".06em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Your Caller ID</label>
            <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)", fontSize: 12, color: "rgba(250,250,250,0.5)" }}>
              {user.assignedNumber || "Using org default"}
            </div>
            <div style={{ fontSize: 9, color: "rgba(250,250,250,0.25)", marginTop: 4 }}>Set by admin in Settings → Phone Numbers.</div>
          </div>
        </div>
      </div>

      {/* Security */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: changingPw ? 16 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>Security</div>
            <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", marginTop: 2 }}>Manage your password and security settings</div>
          </div>
          {!changingPw && <button onClick={() => setChangingPw(true)} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Change Password</button>}
        </div>
        {changingPw && (
          <div>
            {pwMsg && <div style={{ fontSize: 12, color: "#E53935", padding: "8px 12px", background: "rgba(139,69,87,0.08)", borderRadius: 8, marginBottom: 10 }}>{pwMsg}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>Current Password</label><input type="password" style={inp} value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} /></div>
              <div><label style={lbl}>New Password</label><input type="password" style={inp} value={pwForm.new1} onChange={e => setPwForm(f => ({ ...f, new1: e.target.value }))} /></div>
              <div><label style={lbl}>Confirm Password</label><input type="password" style={inp} value={pwForm.new2} onChange={e => setPwForm(f => ({ ...f, new2: e.target.value }))} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handlePwChange} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Update Password</button>
              <button onClick={() => { setChangingPw(false); setPwMsg(""); }} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.5)", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div style={{ ...card, borderColor: "rgba(139,69,87,0.2)" }}>
        <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Danger Zone</div>
        <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", marginBottom: 12 }}>Irreversible account actions</div>
        <button onClick={() => { if (window.confirm("Are you sure you want to sign out?")) auth.logout(); }} style={{
          padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.3)",
          background: "transparent", color: "#E53935", fontSize: 12, fontWeight: 400,
          cursor: "pointer", fontFamily: "inherit",
        }}>Sign Out</button>
      </div>
    </div>
  );
}
